import { apiSlice } from "../apiSlice";
import { API, API_SLICE_TAGS } from "../../../globals/globals";
import { sanitizeQueryParams } from "../../helpers/helpers";

export interface IGoldenAuditDefinitionSummary {
	id: number;
	templatename?: string;
	templateName?: string;
	name: string;
	description?: string;
	version?: string;
	nodeType: string;
	radioTech: string;
	updatedAt?: number;
	lastRun?: number;
	lastRunStatus?: "PASS" | "FAIL" | "SKIPPED";
	scheduleEnabled?: boolean;
	passCount?: number;
	failCount?: number;
	skipCount?: number;
}

export interface IGoldenAuditArgs {
	filters: string;
}

export interface IGoldenTemplateSummary {
	id: number;
	name: string;
	description?: string;
	nodeType: string;
	radioTech: string;
	version?: string;
}

export interface IUpdateGoldenAuditDescriptionPayload {
	id: number;
	description: string;
}

export interface IUpdateGoldenAuditDescriptionResponse {
	id: number;
	description?: string;
	updatedAt?: number;
}

export interface IUpdateGoldenAuditSchedulePayload {
	id: number;
	scheduleEnabled: boolean;
}

export interface IUpdateGoldenAuditScheduleResponse {
	id: number;
	scheduleEnabled?: boolean;
	updatedAt?: number;
}

export interface ICreateAuditDefinitionPayload {
	templateId?: number;
	templateName?: string;
	scopeType: string;
	scheduleEnabled: boolean;
	scheduleIntervalMinutes?: number;
	nodeType?: string;
	radioTech?: string;
	scheduleStartTime?: number;
}

export const goldenAuditApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getGoldenAuditDefinitions: builder.query<IGoldenAuditDefinitionSummary[], IGoldenAuditArgs>({
			query: (args) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.path}?${sanitizeQueryParams(args.filters)}`,
			}),
			providesTags: [API_SLICE_TAGS.GOLDEN_AUDIT_DEFINITIONS],
		}),
		getGoldenTemplates: builder.query<IGoldenTemplateSummary[], void>({
			query: () => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_TEMPLATES.path}`,
			}),
		}),
		deleteGoldenAuditDefinition: builder.mutation<void, number>({
			query: (id) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.path}/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: [API_SLICE_TAGS.GOLDEN_AUDIT_DEFINITIONS],
		}),
		createGoldenAuditDefinition: builder.mutation<any, ICreateAuditDefinitionPayload>({
			query: (payload) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.path}`,
				method: "POST",
				body: payload,
			}),
			invalidatesTags: [API_SLICE_TAGS.GOLDEN_AUDIT_DEFINITIONS],
		}),
		updateGoldenAuditDescription: builder.mutation<IUpdateGoldenAuditDescriptionResponse, IUpdateGoldenAuditDescriptionPayload>({
			query: ({ id, description }) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.path}/${id}/description`,
				method: "PATCH",
				body: { description },
			}),
			invalidatesTags: [API_SLICE_TAGS.GOLDEN_AUDIT_DEFINITIONS],
		}),
		updateGoldenAuditSchedule: builder.mutation<IUpdateGoldenAuditScheduleResponse, IUpdateGoldenAuditSchedulePayload>({
			query: ({ id, scheduleEnabled }) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_DEFINITIONS.path}/${id}/schedule`,
				method: "PATCH",
				body: { scheduleEnabled },
			}),
			invalidatesTags: [API_SLICE_TAGS.GOLDEN_AUDIT_DEFINITIONS],
		}),
		runGoldenAuditNow: builder.mutation<void, number>({
			query: (id) => ({
				url: `${API.ENDPOINTS.GOLDEN_AUDIT_TEMPLATES.path}/${id}/run`,
				method: "POST",
			}),
		}),
	}),
});

export const {
	useGetGoldenAuditDefinitionsQuery,
	useGetGoldenTemplatesQuery,
	useDeleteGoldenAuditDefinitionMutation,
	useCreateGoldenAuditDefinitionMutation,
	useUpdateGoldenAuditDescriptionMutation,
	useUpdateGoldenAuditScheduleMutation,
	useRunGoldenAuditNowMutation,
} = goldenAuditApiSlice;
